export interface NotificationsPagination {
  limit: number
  start: number
}

export interface UseEmployerNotificationsOptions {
  key: string
  fetch: (pagination: NotificationsPagination) => Promise<{ data: NotificationItem[] }>
  limit?: number
  server?: boolean
  refreshInterval?: number
  activeRefreshInterval?: number
}

type NotificationItem = Record<string, unknown> & { id?: number | string }

/** Default idle poll — badge stays reasonably fresh without hammering the API. */
export const NOTIFICATIONS_IDLE_REFRESH_MS = 30_000
/** Poll while any consumer is actively viewing notifications. */
export const NOTIFICATIONS_ACTIVE_REFRESH_MS = 10_000

type NotificationPollSubscriber = {
  refresh: () => void
  isActive: () => boolean
  idleMs: number
  activeMs: number
}

/** Module-level poller shared by the bell menu and ATS dashboard. */
const pollSubscribers = new Set<NotificationPollSubscriber>()
let pollTimer: ReturnType<typeof setInterval> | undefined
let pollListenersBound = false

function clearPollTimer() {
  if (pollTimer === undefined) return
  clearInterval(pollTimer)
  pollTimer = undefined
}

function desiredPollIntervalMs() {
  let anyActive = false
  let idleMs = Number.POSITIVE_INFINITY
  let activeMs = Number.POSITIVE_INFINITY
  for (const subscriber of pollSubscribers) {
    idleMs = Math.min(idleMs, subscriber.idleMs)
    activeMs = Math.min(activeMs, subscriber.activeMs)
    if (subscriber.isActive()) anyActive = true
  }
  if (!pollSubscribers.size) return null
  return anyActive ? activeMs : idleMs
}

function tickPollSubscribers() {
  for (const subscriber of pollSubscribers) subscriber.refresh()
}

function rescheduleNotificationPoller() {
  clearPollTimer()
  if (!import.meta.client || !pollSubscribers.size) return
  if (document.visibilityState === 'hidden') return
  const ms = desiredPollIntervalMs()
  if (ms == null || !Number.isFinite(ms)) return
  pollTimer = setInterval(tickPollSubscribers, ms)
}

function onNotificationPollVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    clearPollTimer()
    return
  }
  tickPollSubscribers()
  rescheduleNotificationPoller()
}

function onNotificationPollFocus() {
  if (document.visibilityState === 'hidden') return
  tickPollSubscribers()
}

function ensurePollListeners() {
  if (pollListenersBound || !import.meta.client) return
  pollListenersBound = true
  window.addEventListener('focus', onNotificationPollFocus)
  document.addEventListener('visibilitychange', onNotificationPollVisibilityChange)
}

function tearDownPollListenersIfIdle() {
  if (pollSubscribers.size || !pollListenersBound || !import.meta.client) return
  pollListenersBound = false
  window.removeEventListener('focus', onNotificationPollFocus)
  document.removeEventListener('visibilitychange', onNotificationPollVisibilityChange)
}

function subscribeNotificationPoller(subscriber: NotificationPollSubscriber) {
  pollSubscribers.add(subscriber)
  ensurePollListeners()
  rescheduleNotificationPoller()
  return {
    reschedule: rescheduleNotificationPoller,
    unsubscribe() {
      pollSubscribers.delete(subscriber)
      if (pollSubscribers.size) {
        rescheduleNotificationPoller()
        return
      }
      clearPollTimer()
      tearDownPollListenersIfIdle()
    }
  }
}

/**
 * Employer-scoped notification list paginated by scroll: the next page is fetched when
 * `loadMoreTrigger` enters `scrollable`. Shared by the bell menu and the ATS dashboard,
 * which only differ by their Strapi query, so callers keep ownership of it.
 *
 * Polling only re-fetches the first page and merges it into the loaded list (no back
 * unread-count endpoint yet). A single module-level timer drives all consumers: it
 * pauses when the tab is hidden, slows down when idle, and speeds up when any consumer
 * sets `active`. Focus still triggers an immediate refresh for every subscriber.
 *
 * The trigger element must be rendered inside the scrollable container, with a real
 * height and in the flow: a zero-height target is never reported as intersecting.
 *
 * The employer is watched through a getter because `fetchUser()` replaces the whole user
 * object: reading `ats.value.employer` once would watch a detached object and never fire.
 */
export async function useEmployerNotifications({
  key,
  fetch,
  limit = 25,
  server,
  refreshInterval = NOTIFICATIONS_IDLE_REFRESH_MS,
  activeRefreshInterval = NOTIFICATIONS_ACTIVE_REFRESH_MS
}: UseEmployerNotificationsOptions) {
  const ats = useAts()

  const scrollable = ref<HTMLElement | null>(null)
  const loadMoreTrigger = ref<HTMLElement | null>(null)
  const pagination = reactive<NotificationsPagination>({ limit, start: 0 })
  /** Callers set this when the list is actively viewed (open menu, visible panel). */
  const active = ref(false)

  let refreshing = false
  let disposed = false

  const notifications = reactive({
    ...(await useLazyAsyncData<NotificationItem[]>(
      key,
      async () => {
        const res = await fetch(pagination)

        return pagination.start > 0 ? [...notifications.data, ...res.data] : res.data
      },
      {
        default: () => [],
        server,
        watch: [pagination]
      }
    ))
  })

  function loadMore() {
    if (notifications.status === 'pending' || notifications.data.length < pagination.start + pagination.limit) return
    pagination.start += pagination.limit
  }

  whenever(useElementVisibility(loadMoreTrigger, { scrollTarget: scrollable }), loadMore)

  watch(
    () => ats.value.employer?.id,
    () => {
      if (pagination.start) pagination.start = 0
      else notifications.refresh()
    }
  )

  /** Keep older scrolled pages; replace/update the head with the fresh first page. */
  function mergeFirstPage(latest: NotificationItem[]) {
    if (pagination.start === 0) {
      notifications.data = latest
      return
    }
    const latestIds = new Set(latest.map(item => item.id))
    const rest = notifications.data.filter(item => !latestIds.has(item.id))
    notifications.data = [...latest, ...rest]
  }

  async function refreshLatest() {
    if (disposed || refreshing || notifications.status === 'pending' || !ats.value.employer?.id) return
    if (import.meta.client && document.visibilityState === 'hidden') return
    refreshing = true
    const employerId = ats.value.employer.id
    const start = pagination.start
    try {
      const res = await fetch({ start: 0, limit })
      if (!disposed && ats.value.employer?.id === employerId && pagination.start === start && notifications.status !== 'pending') {
        mergeFirstPage(res.data)
        notifications.error = null
        notifications.status = 'success'
      }
    } catch {
      // Keep the last successful list; the next refresh retries.
    } finally {
      refreshing = false
    }
  }

  const subscriber: NotificationPollSubscriber = {
    refresh: () => {
      void refreshLatest()
    },
    isActive: () => active.value,
    idleMs: refreshInterval,
    activeMs: activeRefreshInterval
  }

  let unsubscribePoller: (() => void) | undefined

  onMounted(() => {
    const { unsubscribe, reschedule } = subscribeNotificationPoller(subscriber)
    unsubscribePoller = unsubscribe
    watch(active, reschedule)
  })

  onScopeDispose(() => {
    disposed = true
    unsubscribePoller?.()
  })

  return { notifications, scrollable, loadMoreTrigger, active, refreshLatest }
}
